import { CartAddEvent } from '@theme/events';

// Add-to-cart for the cart drawer cross-sell row (snippets/kc-cart-cross-sell.liquid).
// Loaded once per page from snippets/header-actions.liquid; event delegation keeps
// it working after drawer morphs.
//
// Flow: one request to /cart/add.js that also returns the re-rendered cart section
// -> button shows the added state -> the card collapses -> CartAddEvent, which the
// cart-items component uses to morph the drawer once (rows are keyed by id, so only
// the new row is inserted and animates in). kc-auto-gift re-syncs gifts on the event.

const SHOW_ADDED_MS = 220;
const COLLAPSE_MS = 200;

function cartRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
}

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

async function addToCart(button) {
  if (button.getAttribute('aria-busy') === 'true') return;

  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;

  try {
    const sectionIds = getCartSectionIds();
    const response = await fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      console.error('kc-cross-sell add failed', data);
      button.dataset.state = 'error';
      setTimeout(() => {
        delete button.dataset.state;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }, 1500);
      return;
    }

    button.dataset.state = 'added';

    // Let the customer see the confirmation, then fold the card away before the
    // drawer updates, so the row closes smoothly instead of the cards jumping.
    const card = button.closest('.kc-xs__item');
    if (card && !prefersReducedMotion()) {
      await wait(SHOW_ADDED_MS);
      card.classList.add('kc-xs__item--leaving');
      await wait(COLLAPSE_MS);
    }

    // Empty resource on purpose: listeners that need the full cart (kc-auto-gift)
    // then fetch /cart.js instead of trusting the partial add.js response.
    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'kc-cart-cross-sell',
        itemCount: 1,
        productId: button.dataset.productId,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('kc-cross-sell error', error);
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

if (!window.__kcCrossSellBound) {
  window.__kcCrossSellBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-kc-xs-add]') : null;
    if (!target) return;
    event.preventDefault();
    addToCart(target);
  });
}
